import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, ZoomIn } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "all", label: "All Projects" },
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "airbnb", label: "Airbnb" },
  { value: "post-renovation", label: "Post-Renovation" }
];

export default function GalleryPage() {
  const [gallery, setGallery] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  const fetchGallery = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const filteredGallery = useMemo(() => 
    activeCategory === "all"
      ? gallery
      : gallery.filter(item => item.service_type === activeCategory),
    [activeCategory, gallery]
  );

  const featuredItems = useMemo(() => 
    gallery.filter(g => g.featured).slice(0, 2),
    [gallery]
  );

  return (
    <div data-testid="gallery-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Our Work
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              Project Portfolio
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              Browse our portfolio of completed cleaning projects. Each image showcases the quality and attention to detail that Toronto Premium Cleaning delivers.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Category Tabs */}
          <Tabs defaultValue="all" className="mb-12" onValueChange={setActiveCategory}>
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto p-0">
              {categories.map((category) => (
                <TabsTrigger
                  key={category.value}
                  value={category.value}
                  data-testid={`tab-${category.value}`}
                  className="px-6 py-3 rounded-full border border-[#E7E5E4] data-[state=active]:bg-[#064E3B] data-[state=active]:text-white data-[state=active]:border-[#064E3B] text-[#78716C] hover:border-[#064E3B] transition-colors"
                >
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredGallery.map((item, index) => (
              <Dialog key={item.id}>
                <DialogTrigger asChild>
                  <div
                    className="gallery-item cursor-pointer group rounded-xl overflow-hidden shadow-lg bg-white"
                    data-testid={`gallery-item-${index}`}
                  >
                    <div className="relative aspect-[4/3]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="gallery-overlay flex items-end justify-between p-6">
                        <div>
                          <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                          <p className="text-white/80 text-sm capitalize">{item.service_type.replace('-', ' ')}</p>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-[#C0A062] flex items-center justify-center">
                          <ZoomIn className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-4xl p-0 bg-white rounded-2xl overflow-hidden">
                  <div className="relative">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full max-h-[70vh] object-contain bg-[#FAFAF9]"
                    />
                  </div>
                  <div className="p-6 border-t border-[#E7E5E4]">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-[#064E3B] text-white text-xs font-medium rounded-full capitalize">
                        {item.service_type.replace('-', ' ')}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#0C0A09] mb-2">{item.title}</h3>
                    <p className="text-[#78716C]">{item.description}</p>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>

          {filteredGallery.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#78716C] text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
              Featured Work
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              Our Best Transformations
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredItems.map((item) => (
              <div key={item.id} className="group" data-testid={`featured-${item.id}`}>
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <div>
                      <span className="px-3 py-1 bg-[#C0A062] text-white text-xs font-medium rounded-full capitalize mb-2 inline-block">
                        {item.service_type.replace('-', ' ')}
                      </span>
                      <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                      <p className="text-white/80 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
            Ready for Your Transformation?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Let us show you what we can do for your space. Book a cleaning today and experience the Toronto Premium Cleaning difference.
          </p>
          <Link to="/booking" data-testid="gallery-book-btn">
            <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#C0A062]/30">
              Book Your Cleaning
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
